import {
  Station,
  FormOptions,
  FilterOptions,
  StationFilters,
  StationListResponse,
  UpdateStationRequest
} from '@/types/azk-list.types';
import { ApiResponse } from '@/types/services';

import { baseApi } from './base-api';
import { buildQueryParams } from './helpers';
import { apiService, downloadBlob } from './api';

export type GetAzkListFiltersResponse = ApiResponse<FilterOptions>;
export type GetStationsListResponse = ApiResponse<StationListResponse>;

export const azkListApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getAzkListFilters: builder.query<FilterOptions, StationFilters>({
      query: filters => {
        const queryString = buildQueryParams(filters);

        return { url: `azk-list/filters${queryString ? `?${queryString}` : ''}` };
      },
      transformResponse: (response: GetAzkListFiltersResponse): FilterOptions => {
        return response.data;
      }
    }),
    getStationsList: builder.query<StationListResponse, StationFilters>({
      query: filters => {
        const queryString = buildQueryParams(filters);
        return { url: `azk-list${queryString ? `?${queryString}` : ''}` };
      },
      providesTags: ['AzkList'],
      transformResponse: (response: GetStationsListResponse): StationListResponse => {
        return {
          stations: response.data.stations || [],
          total: response.data.total || 0,
          page: response.data.page || 1,
          limit: response.data.limit || 10,
          totalPages: response.data.totalPages || 0
        };
      }
    }),
    getAzkListFormOptions: builder.query<FormOptions, void>({
      query: () => ({ url: `azk-list/form-options` }),
      transformResponse: (response: ApiResponse<FormOptions>): FormOptions => {
        return response.data;
      }
    }),
    getStationProfile: builder.query<Station, string>({
      query: (unicode: string) => ({ url: `azk-list/${unicode}` }),
      providesTags: ['StationProfile'],
      transformResponse: (response: ApiResponse<Station>): Station => {
        return response.data;
      }
    }),
    deleteStation: builder.mutation<boolean, string>({
      query: (unicode: string) => ({
        url: `azk-list/${unicode}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['AzkList', 'StationProfile']
    }),
    updateStationProfile: builder.mutation<Station, { unicode: string; data: UpdateStationRequest }>({
      query: ({ unicode, data }) => ({
        url: `azk-list/${unicode}`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: ['AzkList', 'StationProfile']
    }),
    exportAzkListToExcel: builder.mutation<boolean, StationFilters>({
      async queryFn(filters = {}) {
        try {
          const queryString = buildQueryParams(filters);
          const { blob, filename } = await apiService.getBlob(
            `/azk-list/export${queryString ? `?${queryString}` : ''}`
          );
          downloadBlob(blob, filename);
          return { data: true };
        } catch (error) {
          return { error: error as any };
        }
      }
    })
  })
});
export const {
  useGetStationsListQuery,
  useDeleteStationMutation,
  useGetAzkListFiltersQuery,
  useGetStationProfileQuery,
  useGetAzkListFormOptionsQuery,
  useUpdateStationProfileMutation,
  useExportAzkListToExcelMutation
} = azkListApi;
